import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Snackbar,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, Save as SaveIcon } from '@mui/icons-material';
import api from '../../services/api';

const ClearanceSettings = () => {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [openDialog, setOpenDialog] = useState(false);
  const [editingSetting, setEditingSetting] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [settingToDelete, setSettingToDelete] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    percentage: '',
    exam_type: '',
    is_active: true,
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await api.get('/clearance/settings');
      if (response.data.length > 0) {
        setSettings(response.data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (setting = null) => {
    if (setting) {
      setEditingSetting(setting);
      setFormData({
        name: setting.name,
        percentage: setting.percentage,
        exam_type: setting.exam_type,
        is_active: setting.is_active,
      });
    } else {
      setEditingSetting(null);
      setFormData({
        name: '',
        percentage: '',
        exam_type: '',
        is_active: true,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingSetting(null);
    setFormData({ name: '', percentage: '', exam_type: '', is_active: true });
  };

  const handleSave = async () => {
    if (!formData.name || !formData.percentage || !formData.exam_type) {
      setSnackbar({ open: true, message: 'All fields are required', severity: 'error' });
      return;
    }

    setSaving(true);

    try {
      if (editingSetting) {
        await api.put(`/clearance/settings/${editingSetting.id}`, formData);
        setSnackbar({ open: true, message: 'Setting updated successfully', severity: 'success' });
      } else {
        await api.post('/clearance/settings', formData);
        setSnackbar({ open: true, message: 'Setting created successfully', severity: 'success' });
      }
      fetchSettings();
      handleCloseDialog();
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Failed to save setting', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDeleteDialog = (setting) => {
    setSettingToDelete(setting);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setSettingToDelete(null);
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/clearance/settings/${settingToDelete.id}`);
      setSnackbar({ open: true, message: 'Setting deleted successfully', severity: 'success' });
      handleCloseDeleteDialog();
      fetchSettings();
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Failed to delete setting', severity: 'error' });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight="bold">
          Clearance Settings
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Setting
        </Button>
      </Box>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Percentage (%)</TableCell>
                <TableCell>Exam Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell width={120}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {settings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No clearance settings found. Click "Add Setting" to create one.
                  </TableCell>
                </TableRow>
              ) : (
                settings.map((setting) => (
                  <TableRow key={setting.id}>
                    <TableCell>{setting.name}</TableCell>
                    <TableCell>{setting.percentage}%</TableCell>
                    <TableCell>
                      {setting.exam_type === 'beginning' && 'Beginning'}
                      {setting.exam_type === 'mid_term' && 'Mid Term'}
                      {setting.exam_type === 'end_term' && 'End Term'}
                    </TableCell>
                    <TableCell>
                      {setting.is_active ? (
                        <Typography color="success.main" variant="body2">Active</Typography>
                      ) : (
                        <Typography color="error.main" variant="body2">Inactive</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => handleOpenDialog(setting)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleOpenDeleteDialog(setting)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingSetting ? 'Edit Clearance Setting' : 'Add Clearance Setting'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Beginning of Term"
            />
            <TextField
              fullWidth
              label="Percentage"
              type="number"
              value={formData.percentage}
              onChange={(e) => setFormData({ ...formData, percentage: e.target.value })}
              inputProps={{ min: 0, max: 100, step: 0.01 }}
            />
            <TextField
              fullWidth
              select
              SelectProps={{ native: true }}
              label="Exam Type"
              value={formData.exam_type}
              onChange={(e) => setFormData({ ...formData, exam_type: e.target.value })}
            >
              <option value="">Select Type</option>
              <option value="beginning">Beginning</option>
              <option value="mid_term">Mid Term</option>
              <option value="end_term">End Term</option>
            </TextField>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Active (Only one can be active at a time)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this clearance setting <strong>{settingToDelete?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {settings.length > 0 && (
        <Box mt={3}>
          <Alert severity="info">
            <Typography variant="body2">
              <strong>Note:</strong> These settings determine the minimum fee payment percentage required for students
              to receive clearance cards for different examination periods. Only one clearance setting can be active at a time.
              When you activate a setting, all others will be automatically deactivated.
            </Typography>
          </Alert>
        </Box>
      )}

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ClearanceSettings;
